import type { AuthController, AuthState } from '../controllers/auth-controller.js';
import type { ApiClient } from '../client/api.js';
import type { MeProfile } from '../contract/auth.js';

/**
 * DOM layer: renders the current AuthState and wires form intents to the
 * controller. Native forms + labels keep keyboard and screen-reader behavior
 * correct by construction. No credential ever renders into the DOM.
 */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  node.append(...children);
  return node;
}

function field(
  label: string,
  name: string,
  type: string,
  autocomplete: string,
  extra: Record<string, string> = {},
): HTMLElement {
  const id = `f-${name}`;
  return el(
    'p',
    { class: 'field' },
    el('label', { for: id }, label),
    el('input', { id, name, type, autocomplete, required: '', ...extra }),
  );
}

function messages(state: AuthState): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (state.error) out.push(el('p', { class: 'error', role: 'alert' }, state.error));
  if (state.notice) out.push(el('p', { class: 'notice', role: 'status' }, state.notice));
  return out;
}

function form(onSubmit: (data: FormData) => void, ...children: (Node | string)[]): HTMLFormElement {
  const f = el('form', { novalidate: '' }, ...children);
  f.addEventListener('submit', (ev) => {
    ev.preventDefault();
    onSubmit(new FormData(f));
  });
  return f;
}

// FormData entries can be Files; our forms only carry text inputs.
const str = (data: FormData, name: string): string => {
  const v = data.get(name);
  return typeof v === 'string' ? v.trim() : '';
};
const raw = (data: FormData, name: string): string => {
  const v = data.get(name);
  return typeof v === 'string' ? v : '';
};

export function renderAuth(
  root: HTMLElement,
  state: AuthState,
  controller: AuthController,
  api: ApiClient,
): void {
  root.replaceChildren();
  const busyAttrs: Record<string, string> = state.busy ? { disabled: '', 'aria-busy': 'true' } : {};
  const submit = (label: string): HTMLElement =>
    el('p', {}, el('button', { type: 'submit', class: 'primary', ...busyAttrs }, label));
  const link = (label: string, go: () => void): HTMLElement => {
    const b = el('button', { type: 'button', class: 'link' }, label);
    b.addEventListener('click', go);
    return b;
  };

  switch (state.screen) {
    case 'signin': {
      root.append(
        el('h1', {}, 'Sign in to GiftMotor'),
        ...messages(state),
        form(
          (d) => void controller.signIn(str(d, 'email'), raw(d, 'password')),
          field('Email', 'email', 'email', 'email'),
          field('Password', 'password', 'password', 'current-password'),
          submit('Sign in'),
        ),
        el(
          'p',
          { class: 'alt' },
          link('Create an account', () => controller.goTo('signup')),
          ' · ',
          link('Forgot password?', () => controller.goTo('forgot')),
        ),
      );
      break;
    }
    case 'signup': {
      root.append(
        el('h1', {}, 'Create your GiftMotor account'),
        ...messages(state),
        el(
          'p',
          { class: 'hint' },
          'Passwords need at least 12 characters, with a lowercase letter and a number.',
        ),
        form(
          (d) => void controller.signUp(str(d, 'email'), raw(d, 'password'), raw(d, 'password2')),
          field('Email', 'email', 'email', 'email'),
          field('Password', 'password', 'password', 'new-password', { minlength: '12' }),
          field('Repeat password', 'password2', 'password', 'new-password', { minlength: '12' }),
          submit('Create account'),
        ),
        el(
          'p',
          { class: 'alt' },
          link('Back to sign in', () => controller.goTo('signin')),
        ),
      );
      break;
    }
    case 'confirm': {
      root.append(
        el('h1', {}, 'Confirm your email'),
        ...messages(state),
        form(
          (d) => void controller.confirmSignUp(str(d, 'code')),
          field('Confirmation code', 'code', 'text', 'one-time-code', { inputmode: 'numeric' }),
          submit('Confirm'),
        ),
        el(
          'p',
          { class: 'alt' },
          link('Send a new code', () => void controller.resendCode()),
          ' · ',
          link('Back to sign in', () => controller.goTo('signin')),
        ),
      );
      break;
    }
    case 'forgot': {
      root.append(
        el('h1', {}, 'Reset your password'),
        ...messages(state),
        form(
          (d) => void controller.forgotPassword(str(d, 'email')),
          field('Email', 'email', 'email', 'email'),
          submit('Send reset code'),
        ),
        el(
          'p',
          { class: 'alt' },
          link('Back to sign in', () => controller.goTo('signin')),
        ),
      );
      break;
    }
    case 'reset': {
      root.append(
        el('h1', {}, 'Choose a new password'),
        ...messages(state),
        form(
          (d) =>
            void controller.resetPassword(str(d, 'code'), raw(d, 'password'), raw(d, 'password2')),
          field('Reset code', 'code', 'text', 'one-time-code', { inputmode: 'numeric' }),
          field('New password', 'password', 'password', 'new-password', { minlength: '12' }),
          field('Repeat new password', 'password2', 'password', 'new-password', {
            minlength: '12',
          }),
          submit('Set new password'),
        ),
        el(
          'p',
          { class: 'alt' },
          link('Back to sign in', () => controller.goTo('signin')),
        ),
      );
      break;
    }
    case 'home': {
      const who = el('p', { class: 'hint' }, 'Loading your account…');
      root.append(
        el('h1', {}, 'GiftMotor'),
        ...messages(state),
        who,
        el(
          'p',
          {},
          (() => {
            const b = el('button', { type: 'button', class: 'primary', ...busyAttrs }, 'Sign out');
            b.addEventListener('click', () => void controller.signOut());
            return b;
          })(),
        ),
      );
      api
        .me()
        .then((me: MeProfile) => {
          who.textContent = `Signed in as ${me.displayName} (${me.email})`;
        })
        .catch((err: unknown) => {
          who.textContent = err instanceof Error ? err.message : 'Could not load your account.';
          who.classList.add('error');
        });
      break;
    }
  }
  root.querySelector('input')?.focus();
}
